// Contract creation wizard. Either side can initiate. Other party is
// looked up by userId (we mock the lookup against a tiny directory).
//
// Three sections:
//   1. Basics (title / amount / other party)
//   2. Milestones — percent-driven; tail row at the bottom (10/15/20)
//   3. Payment checkpoints — pick ≥2 trigger points; amounts are
//      computed automatically from the milestones each checkpoint covers.

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Search, Info } from 'lucide-react';
import { ContractsV2Shell } from './DemoShell';
import { useContractsV2 } from './ContractsV2Context';
import {
  SMALL_PROJECT_THRESHOLD,
  TAIL_PERCENT_OPTIONS,
  MIN_PAYMENT_CHECKPOINTS,
  ACCEPTANCE_PERIOD_DEFAULTS, ACCEPTANCE_PERIOD_COMPANY_BONUS,
} from './mockTypes';
import type {
  ContractV2Bundle, Milestone, PaymentPlanEntry, ReleasePlanEntry,
  PaymentEntryTrigger, AccountType, TailPercent,
} from './mockTypes';

// Tiny userId → nickname/accountType directory (demo only).
const DIRECTORY: Record<string, { nickname: string; accountType: AccountType }> = {
  demo_client_001:  { nickname: '北方熊广告',     accountType: 'company' },
  demo_creator_001: { nickname: '林小川',          accountType: 'individual' },
  demo_client_002:  { nickname: '青葵传媒',        accountType: 'company' },
  demo_creator_002: { nickname: '夏目工作室',      accountType: 'individual' },
};

function lookup(userId: string) { return DIRECTORY[userId]; }

const day = 24 * 3600 * 1000;
const fmtRmb = (n: number) => `¥${Math.round(n).toLocaleString('zh-CN')}`;

// =====================================================================
// Form state types
// =====================================================================

interface MilestoneDraft {
  title: string;
  description: string;
  percent: number;        // user-edited
  daysFromNow: number;
  acceptanceCriteria: string;
  assets: string[];
}

// User picks payment checkpoints by directly choosing which milestones
// each payment covers (must be a contiguous block, in order). Coverage
// determines both the amount and the trigger:
//   covers M1+ (block starting at M1) → trigger is "合同签署后"
//   covers Mi+ where i>1                → trigger is "M(i-1) 验收后"
//   covers tail only                    → trigger is "最终验收后"
interface CheckpointDraft {
  coversMilestoneIndices: number[]; // contiguous; empty allowed only for tail-only payment
  coversTail: boolean;
}

// =====================================================================
// Page
// =====================================================================

const ContractCreatePage: React.FC = () => {
  const nav = useNavigate();
  const { dispatch, viewerId } = useContractsV2();

  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1 — basics
  const myInfo = lookup(viewerId);
  const [myRole, setMyRole] = useState<'client' | 'creator'>('client');
  const [otherUserId, setOtherUserId] = useState('');
  const [otherInfo, setOtherInfo] = useState<{ nickname: string; accountType: AccountType } | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [totalAmount, setTotalAmount] = useState(5000);
  // Invoice is a contract-level setting, not per-payment. Default on
  // when the buyer is an enterprise account, off otherwise.
  const [requiresInvoice, setRequiresInvoice] = useState(false);

  const projectType = totalAmount < SMALL_PROJECT_THRESHOLD ? 'small' : 'large';
  const buyerAccountType: AccountType =
    myRole === 'client' ? (myInfo?.accountType || 'individual') : (otherInfo?.accountType || 'individual');
  const acceptancePeriodDays = ACCEPTANCE_PERIOD_DEFAULTS[projectType] +
    (buyerAccountType === 'company' ? ACCEPTANCE_PERIOD_COMPANY_BONUS : 0);

  // Step 2 — milestones + tail
  const [milestones, setMilestones] = useState<MilestoneDraft[]>([
    { title: 'M1', description: '', percent: 50, daysFromNow: 7, acceptanceCriteria: '', assets: [] },
    { title: 'M2', description: '', percent: 30, daysFromNow: 14, acceptanceCriteria: '', assets: [] },
  ]);
  const [tailPercent, setTailPercent] = useState<TailPercent>(20);

  const milestonePercentSum = milestones.reduce((s, m) => s + (m.percent || 0), 0);
  const totalPercentSum = milestonePercentSum + tailPercent;
  const balanced = totalPercentSum === 100;

  // Step 3 — payment checkpoints
  const [checkpoints, setCheckpoints] = useState<CheckpointDraft[]>([]);

  // Compute each checkpoint's amount, derived trigger, and label.
  const computedCheckpoints = useMemo(() => {
    return checkpoints.map((cp, i) => {
      const milestonePct = cp.coversMilestoneIndices.reduce(
        (s, idx) => s + (milestones[idx]?.percent || 0), 0
      );
      const tailPct = cp.coversTail ? tailPercent : 0;
      const pct = milestonePct + tailPct;
      const amount = Math.round(totalAmount * pct / 100);

      // Derive trigger from coverage. The earliest milestone in
      // this checkpoint's range tells us "this payment must arrive
      // before that milestone starts."
      const sortedIdx = [...cp.coversMilestoneIndices].sort((a, b) => a - b);
      const firstCovered = sortedIdx[0];
      const triggerLabel =
        cp.coversTail && cp.coversMilestoneIndices.length === 0
          ? '最终验收后（尾款）'
          : firstCovered === 0
            ? '合同签署后'
            : firstCovered !== undefined
              ? `${milestones[firstCovered - 1]?.title || `M${firstCovered}`} 验收后`
              : '（无覆盖）';

      return { cp, index: i, milestoneIndices: sortedIdx, coversTail: cp.coversTail, pct, amount, triggerLabel };
    });
  }, [checkpoints, milestones, tailPercent, totalAmount]);

  const checkpointTotalPct = computedCheckpoints.reduce((s, c) => s + c.pct, 0);
  const allMoneyCovered = checkpointTotalPct === 100;
  const meetsMinCheckpoints = checkpoints.length >= MIN_PAYMENT_CHECKPOINTS;
  const hasTailCheckpoint = computedCheckpoints.some(c => c.coversTail);

  // Indices that a given checkpoint may pick from: all milestones not
  // yet covered by previous checkpoints, except checkpoint i can also
  // include the milestones it currently covers itself (so unchecking
  // works). Returns a set of allowed indices.
  const allowedMilestonesFor = (cpIndex: number): Set<number> => {
    const usedByOthers = new Set<number>();
    checkpoints.forEach((c, i) => {
      if (i === cpIndex) return;
      c.coversMilestoneIndices.forEach(idx => usedByOthers.add(idx));
    });
    const allowed = new Set<number>();
    milestones.forEach((_, i) => {
      if (!usedByOthers.has(i)) allowed.add(i);
    });
    return allowed;
  };

  // The set of milestones this checkpoint covers must be a contiguous
  // block starting either at 0 or at (last milestone of previous
  // checkpoint + 1). To enforce that visually, we compute the
  // "expected start" — anything before that is locked, anything from
  // there onward up to the next-checkpoint's-first is selectable.
  const validateContiguous = (cpIndex: number, indices: number[]): boolean => {
    if (indices.length === 0) return true;
    const sorted = [...indices].sort((a, b) => a - b);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] !== sorted[i - 1] + 1) return false;
    }
    return true;
  };

  // -------------------------------------------------------------------
  // Auto-seed step 3 when the user enters it the first time. Only
  // reached for 大项目 — 小项目 submits directly from step 2.
  // -------------------------------------------------------------------
  const enterStep3 = () => {
    if (checkpoints.length === 0) {
      // Default 大项目: pay 1 covers M1 (minimum required), pay 2
      // covers all remaining milestones, pay 3 covers tail.
      const allRemaining = milestones.map((_, i) => i).slice(1);
      setCheckpoints([
        { coversMilestoneIndices: [0], coversTail: false },
        ...(allRemaining.length > 0 ? [{
          coversMilestoneIndices: allRemaining,
          coversTail: false,
        }] : []),
        { coversMilestoneIndices: [], coversTail: true },
      ]);
    }
    setStep(3);
  };

  const updateMilestone = (i: number, patch: Partial<MilestoneDraft>) => {
    setMilestones(ms => ms.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  };

  const addMilestone = () => {
    setMilestones(ms => [...ms, {
      title: `M${ms.length + 1}`, description: '',
      percent: 0,
      daysFromNow: ms.length === 0 ? 7 : ms[ms.length - 1].daysFromNow + 7,
      acceptanceCriteria: '', assets: [],
    }]);
  };

  const removeMilestone = (i: number) => {
    setMilestones(ms => ms.filter((_, idx) => idx !== i));
    // Drop the removed index from any checkpoint coverage; shift
    // higher indices down by 1.
    setCheckpoints(cps => cps.map(cp => ({
      ...cp,
      coversMilestoneIndices: cp.coversMilestoneIndices
        .filter(idx => idx !== i)
        .map(idx => idx > i ? idx - 1 : idx),
    })));
  };

  const handleSubmit = () => {
    if (!otherInfo) return;
    const myParty = myInfo ? { userId: viewerId, ...myInfo } : { userId: viewerId, nickname: 'Me', accountType: 'individual' as AccountType };
    const otherParty = { userId: otherUserId.trim(), ...otherInfo };
    const client = myRole === 'client' ? myParty : otherParty;
    const creator = myRole === 'creator' ? myParty : otherParty;

    const contractId = `demo_contract_${Date.now().toString(36)}`;
    const now = Date.now();

    const builtMilestones: Milestone[] = projectType === 'small'
      ? [{
          id: `${contractId}_m0`, contractId, sequenceIndex: 0,
          title: '交付', description: description || '单一交付',
          percent: 100, amount: totalAmount,
          dueAt: now + 7 * day,
          acceptanceCriteria: '满足约定标准',
          revisionLimit: 2, revisionCountUsed: 0,
          requiredAssets: [], submittedAssets: [],
          revisionHistory: [],
          status: 'IN_PROGRESS', paymentStatus: 'UNFUNDED',
        }]
      : milestones.map((m, i) => ({
          id: `${contractId}_m${i}`, contractId, sequenceIndex: i,
          title: m.title || `M${i + 1}`, description: m.description,
          percent: m.percent,
          amount: Math.round(totalAmount * m.percent / 100),
          dueAt: now + m.daysFromNow * day,
          acceptanceCriteria: m.acceptanceCriteria,
          revisionLimit: 2, revisionCountUsed: 0,
          requiredAssets: m.assets.map(a => ({ type: 'other' as const, label: a })),
          submittedAssets: [],
          revisionHistory: [],
          status: i === 0 ? 'IN_PROGRESS' : 'NOT_STARTED',
          paymentStatus: 'UNFUNDED',
        }));

    // Small projects: skip the checkpoint wizard entirely. One payment,
    // full amount, fires on contract sign, covers the single milestone.
    const builtPayments: PaymentPlanEntry[] = projectType === 'small'
      ? [{
          id: `${contractId}_p0`,
          contractId,
          sequenceIndex: 0,
          amount: totalAmount,
          coversMilestoneIds: [builtMilestones[0].id],
          coversTail: false,
          trigger: 'CONTRACT_SIGNED',
          status: 'DUE',
          isFinalTail: false,
        }]
      : computedCheckpoints.map((c, i) => {
      const sortedIdx = c.milestoneIndices;
      const firstCovered = sortedIdx[0];
      let trigger: PaymentEntryTrigger;
      let triggerMilestoneId: string | undefined;
      if (c.coversTail && sortedIdx.length === 0) {
        trigger = 'FINAL_TAIL';
        triggerMilestoneId = builtMilestones[builtMilestones.length - 1]?.id;
      } else if (firstCovered === 0) {
        trigger = 'CONTRACT_SIGNED';
      } else {
        trigger = 'MILESTONE_PASSED';
        triggerMilestoneId = builtMilestones[firstCovered - 1]?.id;
      }
      const realMilestoneIds = sortedIdx.map(idx => builtMilestones[idx]?.id).filter((x): x is string => !!x);
      return {
        id: `${contractId}_p${i}`,
        contractId,
        sequenceIndex: i,
        amount: c.amount,
        coversMilestoneIds: realMilestoneIds,
        coversTail: c.coversTail,
        trigger,
        triggerMilestoneId,
        status: i === 0 ? 'DUE' : 'PENDING',
        isFinalTail: c.coversTail,
      };
    });

    // Release plan: one per milestone at the milestone's percent. Tail
    // is its own pseudo-release after the last milestone.
    const builtReleases: ReleasePlanEntry[] = [
      ...builtMilestones.map((m, i) => ({
        id: `${contractId}_r${i}`, contractId, sequenceIndex: i,
        milestoneId: m.id, amount: m.amount, status: 'PENDING' as const,
      })),
    ];

    const tailAmount = projectType === 'large' ? Math.round(totalAmount * tailPercent / 100) : 0;

    const bundle: ContractV2Bundle = {
      contract: {
        id: contractId, initiatorRole: myRole, client, creator,
        title, description,
        projectType, totalAmount,
        tailPercent: projectType === 'large' ? tailPercent : 0,
        tailAmount,
        totalPaid: 0, totalReleased: 0, totalRefunded: 0,
        acceptanceState: 'pending_other_side',
        createdAt: now,
        accountTypeAtSign: client.accountType,
        revisionLimitDefault: 2,
        requiresInvoice,
      },
      milestones: builtMilestones,
      paymentPlan: builtPayments,
      releasePlan: builtReleases,
      changes: [],
      disputes: [],
    };

    dispatch({ type: 'CREATE_BUNDLE', bundle });
    nav(`/contracts-v2/${contractId}`);
  };

  // =====================================================================
  // Render
  // =====================================================================

  return (
    <ContractsV2Shell>
      <button onClick={() => nav('/contracts-v2')} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> 返回列表
      </button>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
        <h1 className="text-xl font-semibold text-slate-900 mb-1">发起新合同</h1>
        <p className="text-sm text-slate-500 mb-5">客户和创作者均可发起；另一方在收到后可同意、拒绝或反提建议。</p>

        <div className="flex items-center gap-2 text-xs">
          <StepDot active={step === 1} done={step > 1} label="1. 基本信息" />
          <Connector />
          <StepDot active={step === 2} done={step > 2} label={projectType === 'small' ? '2. 确认并发送' : '2. 里程碑 + 尾款'} />
          {projectType === 'large' && (
            <>
              <Connector />
              <StepDot active={step === 3} done={false} label="3. 付款节点" />
            </>
          )}
        </div>
      </div>

      {step === 1 && (
        <Card>
          <RoleToggle myRole={myRole} setMyRole={setMyRole} myInfo={myInfo} />

          <div className="mt-5">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              对方用户 ID <span className="text-rose-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                value={otherUserId}
                onChange={e => { setOtherUserId(e.target.value); setOtherInfo(null); }}
                placeholder="例如 demo_client_002"
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
              <button
                onClick={() => { const info = lookup(otherUserId.trim()); setOtherInfo(info || null); }}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50 inline-flex items-center gap-1"
              >
                <Search className="w-3 h-3" /> 查找
              </button>
            </div>
            {otherInfo && (
              <div className="mt-2 inline-flex items-center gap-2 text-xs">
                <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded">{otherInfo.nickname}</span>
                <span className="text-slate-400">{otherInfo.accountType === 'company' ? '企业账号' : '个人账号'} · 已自动填充昵称</span>
              </div>
            )}
            {otherUserId && !otherInfo && (
              <div className="mt-2 text-xs text-rose-600">未找到该用户。Demo 可用：demo_client_002 / demo_creator_002</div>
            )}
          </div>

          <div className="mt-5">
            <label className="block text-sm font-medium text-slate-700 mb-1">合同标题</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="例：品牌短片 60s 全流程制作"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">需求描述</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">项目总金额 (¥)</label>
              <input
                type="number"
                value={totalAmount}
                onChange={e => setTotalAmount(Number(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
              <p className={`text-xs mt-1.5 ${projectType === 'large' ? 'text-purple-700' : 'text-sky-700'}`}>
                {projectType === 'large'
                  ? `≥ ¥${SMALL_PROJECT_THRESHOLD} → 大项目（按里程碑 + 尾款管理）`
                  : `< ¥${SMALL_PROJECT_THRESHOLD} → 小项目（全款托管 / 单一交付）`}
              </p>
            </div>
            <div className="text-xs text-slate-500 pt-7 leading-relaxed">
              <Info className="w-3 h-3 inline-block mr-1 align-middle text-slate-400" />
              验收期：默认 {ACCEPTANCE_PERIOD_DEFAULTS[projectType]} 天
              {buyerAccountType === 'company' && ` + 企业 ${ACCEPTANCE_PERIOD_COMPANY_BONUS} 天`}
              <br />
              当前合约预计验收期：<span className="font-semibold text-slate-700">{acceptancePeriodDays} 天</span>
            </div>
          </div>

          <div className="mt-5 rounded-lg border border-slate-200 p-3">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={requiresInvoice}
                onChange={e => setRequiresInvoice(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium text-slate-700">需要发票</span>
            </label>
            <p className="text-xs text-slate-500 mt-1.5 ml-6">
              企业客户通常需要为每笔付款开具发票。该设置作用于本合同所有付款节点。
            </p>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setStep(2)}
              disabled={!otherInfo || !title.trim() || totalAmount <= 0}
              className="px-4 py-2 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 disabled:opacity-40"
            >
              下一步
            </button>
          </div>
        </Card>
      )}

      {step === 2 && projectType === 'large' && (
        <Card>
          <h2 className="text-base font-semibold text-slate-900 mb-1">里程碑 + 尾款</h2>
          <p className="text-sm text-slate-500 mb-4">
            按比例分配。里程碑比例 + 尾款比例之和必须等于 100%。
          </p>

          <div className="space-y-3 mb-3">
            {milestones.map((m, i) => (
              <MilestoneRow
                key={i}
                index={i}
                milestone={m}
                totalAmount={totalAmount}
                onChange={(patch) => updateMilestone(i, patch)}
                onRemove={milestones.length > 1 ? () => removeMilestone(i) : undefined}
              />
            ))}
          </div>

          <button onClick={addMilestone} className="w-full py-2 border border-dashed border-slate-300 rounded-xl text-sm text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center gap-1.5 mb-4">
            <Plus className="w-4 h-4" /> 添加里程碑
          </button>

          {/* Tail row pinned at the bottom of the milestone list */}
          <TailRow
            tailPercent={tailPercent}
            setTailPercent={setTailPercent}
            totalAmount={totalAmount}
          />

          <div className="mt-5 rounded-lg border p-3 text-sm flex items-center justify-between gap-3"
            style={{ borderColor: balanced ? 'rgb(167 243 208)' : 'rgb(252 165 165)',
                     background: balanced ? 'rgb(236 253 245)' : 'rgb(254 242 242)' }}>
            <div>
              <span className="text-slate-500">里程碑合计 {milestonePercentSum}% + 尾款 {tailPercent}% = </span>
              <span className={`font-semibold ${balanced ? 'text-emerald-700' : 'text-rose-700'}`}>{totalPercentSum}%</span>
            </div>
            <div className={`text-xs ${balanced ? 'text-emerald-700' : 'text-rose-700'}`}>
              {balanced ? '✓ 已分配 100%' : `仍需调整 ${100 - totalPercentSum > 0 ? '+' : ''}${100 - totalPercentSum}%`}
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            <button onClick={() => setStep(1)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">上一步</button>
            <button
              onClick={enterStep3}
              disabled={!balanced}
              className="px-4 py-2 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 disabled:opacity-40"
            >
              下一步
            </button>
          </div>
        </Card>
      )}

      {step === 2 && projectType === 'small' && (
        <Card>
          <h2 className="text-base font-semibold text-slate-900 mb-1">确认并发送</h2>
          <p className="text-sm text-slate-500 mb-4">
            小项目走简化流程：单次交付、一次性付款，无里程碑、无尾款。客户下单时全额支付，平台托管；创作者交付后客户进入验收期，验收通过或超时自动验收后平台放款。
          </p>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 space-y-1.5">
            <div>金额：¥{totalAmount.toLocaleString('zh-CN')}（一次性全额付款）</div>
            <div>验收期：{acceptancePeriodDays} 天</div>
            <div>修改次数：2 次（默认）</div>
            {requiresInvoice && <div>需要发票：是</div>}
          </div>
          <div className="mt-6 flex justify-between">
            <button onClick={() => setStep(1)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">上一步</button>
            <button onClick={handleSubmit} className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700">发送给对方</button>
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <h2 className="text-base font-semibold text-slate-900 mb-1">付款节点</h2>
          <p className="text-sm text-slate-500 mb-5">
            选择客户分几次向平台付款。每个节点的金额由它覆盖的里程碑（和/或尾款）自动计算。最少 {MIN_PAYMENT_CHECKPOINTS} 个节点。
          </p>

          <div className="space-y-3 mb-4">
            {checkpoints.map((cp, i) => {
              const computed = computedCheckpoints[i];
              const allowed = allowedMilestonesFor(i);
              return (
                <CheckpointRow
                  key={i}
                  index={i}
                  checkpoint={cp}
                  computed={computed}
                  milestones={milestones}
                  allowedMilestoneIndices={allowed}
                  hasTailCheckpoint={hasTailCheckpoint}
                  onToggleMilestone={(idx) => {
                    setCheckpoints(arr => arr.map((x, k) => {
                      if (k !== i) return x;
                      const has = x.coversMilestoneIndices.includes(idx);
                      const next = has
                        ? x.coversMilestoneIndices.filter(n => n !== idx)
                        : [...x.coversMilestoneIndices, idx].sort((a, b) => a - b);
                      // Reject if non-contiguous after the change.
                      if (!validateContiguous(i, next)) return x;
                      return { ...x, coversMilestoneIndices: next };
                    }));
                  }}
                  onToggleTail={(v) => {
                    setCheckpoints(arr => arr.map((x, k) =>
                      k === i ? { ...x, coversTail: v } : x
                    ));
                  }}
                  onRemove={() => setCheckpoints(arr => arr.filter((_, idx) => idx !== i))}
                />
              );
            })}
          </div>

          <button
            onClick={() => setCheckpoints(arr => [...arr, { coversMilestoneIndices: [], coversTail: false }])}
            className="w-full py-2 border border-dashed border-slate-300 rounded-xl text-xs text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center gap-1.5 mb-4"
          >
            <Plus className="w-3 h-3" /> 添加付款节点
          </button>

          <div className="rounded-lg border p-3 text-sm flex items-center justify-between gap-3"
            style={{ borderColor: allMoneyCovered ? 'rgb(167 243 208)' : 'rgb(252 165 165)',
                     background: allMoneyCovered ? 'rgb(236 253 245)' : 'rgb(254 242 242)' }}>
            <div>
              <span className="text-slate-500">付款节点覆盖 </span>
              <span className={`font-semibold ${allMoneyCovered ? 'text-emerald-700' : 'text-rose-700'}`}>
                {checkpointTotalPct}%
              </span>
              <span className="text-slate-400"> （应为 100%）</span>
            </div>
            <div className={`text-xs ${meetsMinCheckpoints ? 'text-emerald-700' : 'text-rose-700'}`}>
              {meetsMinCheckpoints ? `✓ ${computedCheckpoints.length} 个节点` : `至少 ${MIN_PAYMENT_CHECKPOINTS} 个节点`}
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            <button onClick={() => setStep(2)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">上一步</button>
            <button
              onClick={handleSubmit}
              disabled={!allMoneyCovered || !meetsMinCheckpoints}
              className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-40"
            >
              发送给对方
            </button>
          </div>
        </Card>
      )}
    </ContractsV2Shell>
  );
};

// =====================================================================
// Sub-components
// =====================================================================

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6">{children}</div>
);

const StepDot: React.FC<{ active: boolean; done: boolean; label: string }> = ({ active, done, label }) => (
  <div className={`px-3 py-1.5 rounded-md ${
    active ? 'bg-slate-900 text-white' : done ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
  }`}>{label}</div>
);

const Connector: React.FC = () => <div className="h-px w-8 bg-slate-200" />;

const RoleToggle: React.FC<{
  myRole: 'client' | 'creator';
  setMyRole: (r: 'client' | 'creator') => void;
  myInfo?: { nickname: string; accountType: AccountType };
}> = ({ myRole, setMyRole, myInfo }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">我作为</label>
    <div className="flex gap-2 mb-1">
      <button onClick={() => setMyRole('client')}
        className={`flex-1 px-3 py-2 rounded-lg text-sm border transition-colors ${
          myRole === 'client' ? 'border-blue-300 bg-blue-50 text-blue-800 font-medium' : 'border-slate-200 hover:bg-slate-50'
        }`}>客户（甲方）</button>
      <button onClick={() => setMyRole('creator')}
        className={`flex-1 px-3 py-2 rounded-lg text-sm border transition-colors ${
          myRole === 'creator' ? 'border-emerald-300 bg-emerald-50 text-emerald-800 font-medium' : 'border-slate-200 hover:bg-slate-50'
        }`}>创作者（乙方）</button>
    </div>
    {myInfo && <p className="text-xs text-slate-400 mt-1">当前账号：{myInfo.nickname} · {myInfo.accountType === 'company' ? '企业' : '个人'}</p>}
  </div>
);

// =====================================================================
// Milestone editor row — percent-driven
// =====================================================================

const MilestoneRow: React.FC<{
  index: number;
  milestone: MilestoneDraft;
  totalAmount: number;
  onChange: (patch: Partial<MilestoneDraft>) => void;
  onRemove?: () => void;
}> = ({ index, milestone, totalAmount, onChange, onRemove }) => {
  const computedAmount = Math.round(totalAmount * (milestone.percent || 0) / 100);
  return (
    <div className="border border-slate-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-500">M{index + 1}</span>
          <input
            value={milestone.title}
            onChange={e => onChange({ title: e.target.value })}
            placeholder="阶段名"
            className="px-2 py-1 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>
        {onRemove && (
          <button onClick={onRemove} className="text-slate-400 hover:text-rose-600">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <textarea
        value={milestone.description}
        rows={2}
        onChange={e => onChange({ description: e.target.value })}
        placeholder="阶段交付物描述..."
        className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
      />
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div>
          <label className="text-[11px] text-slate-500">比例 (%)</label>
          <input
            type="number"
            min={0} max={100}
            value={milestone.percent}
            onChange={e => onChange({ percent: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
            className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
          <div className="text-[10px] text-slate-400 mt-1">≈ {fmtRmb(computedAmount)}</div>
        </div>
        <div>
          <label className="text-[11px] text-slate-500">距合同生效天数</label>
          <input
            type="number"
            value={milestone.daysFromNow}
            onChange={e => onChange({ daysFromNow: Number(e.target.value) || 0 })}
            className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>
        <div>
          <label className="text-[11px] text-slate-500">修改次数</label>
          <input
            type="number" value={2} disabled
            className="w-full px-2 py-1.5 border border-slate-200 bg-slate-50 rounded text-sm text-slate-500"
          />
        </div>
      </div>
      <input
        value={milestone.acceptanceCriteria}
        onChange={e => onChange({ acceptanceCriteria: e.target.value })}
        placeholder="验收标准（双方约定的可交付成果与有效交付定义）"
        className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
      />
    </div>
  );
};

const TailRow: React.FC<{
  tailPercent: TailPercent;
  setTailPercent: (p: TailPercent) => void;
  totalAmount: number;
}> = ({ tailPercent, setTailPercent, totalAmount }) => {
  return (
    <div className="border-2 border-amber-200 bg-amber-50/40 rounded-xl p-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <div className="text-sm font-medium text-amber-900">尾款</div>
          <div className="text-[11px] text-amber-700/80">客户保留至最终资产移交后释放。未到账前不视为已托管资金。</div>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold text-amber-900">≈ {fmtRmb(Math.round(totalAmount * tailPercent / 100))}</div>
          <div className="text-[11px] text-amber-700/80">{tailPercent}%</div>
        </div>
      </div>
      <div className="flex gap-2">
        {TAIL_PERCENT_OPTIONS.map(opt => (
          <button
            key={opt}
            type="button"
            onClick={() => setTailPercent(opt)}
            className={`flex-1 px-3 py-2 rounded-lg text-sm border transition-colors ${
              tailPercent === opt
                ? 'border-amber-400 bg-amber-100 text-amber-900 font-medium'
                : 'border-amber-200 bg-white text-amber-800 hover:bg-amber-50'
            }`}
          >
            {opt}%
          </button>
        ))}
      </div>
    </div>
  );
};

// =====================================================================
// Checkpoint editor row
// =====================================================================

const CheckpointRow: React.FC<{
  index: number;
  checkpoint: CheckpointDraft;
  computed?: {
    milestoneIndices: number[];
    coversTail: boolean;
    pct: number;
    amount: number;
    triggerLabel: string;
  };
  milestones: MilestoneDraft[];
  allowedMilestoneIndices: Set<number>;
  hasTailCheckpoint: boolean;
  onToggleMilestone: (idx: number) => void;
  onToggleTail: (v: boolean) => void;
  onRemove: () => void;
}> = ({ index, checkpoint, computed, milestones, allowedMilestoneIndices,
       hasTailCheckpoint, onToggleMilestone, onToggleTail, onRemove }) => {
  const isTailOnly = checkpoint.coversTail && checkpoint.coversMilestoneIndices.length === 0;

  // Build the set of indices that, if toggled in this row's current
  // state, would yield a contiguous block. A milestone is selectable
  // when it's already selected (so unchecking works), or when adding
  // it keeps the selection contiguous.
  const sortedSel = [...checkpoint.coversMilestoneIndices].sort((a, b) => a - b);
  const minSel = sortedSel[0];
  const maxSel = sortedSel[sortedSel.length - 1];
  const isContiguousIfAdded = (idx: number): boolean => {
    if (sortedSel.length === 0) return true;
    return idx === minSel - 1 || idx === maxSel + 1;
  };

  return (
    <div className={`border rounded-xl p-4 ${isTailOnly ? 'border-amber-200 bg-amber-50/40' : 'border-slate-200'}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[11px] text-slate-400 font-medium mb-0.5">付款 #{index + 1}</div>
          <div className="text-sm font-medium text-slate-900">
            {computed?.triggerLabel || '（待定）'}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-slate-500">付款金额（自动）</div>
          <div className="text-base font-bold text-slate-900">
            {computed ? fmtRmb(computed.amount) : '—'}
          </div>
          <div className="text-[10px] text-slate-400">{computed?.pct ?? 0}%</div>
        </div>
        <button
          onClick={onRemove}
          className="text-slate-400 hover:text-rose-600 mt-0.5"
          title="删除该付款节点"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Coverage selector — the prominent block per user feedback */}
      <div className="rounded-lg bg-blue-50/60 border border-blue-200 p-3">
        <div className="text-[11px] font-semibold text-blue-900 mb-2 tracking-wide">
          覆盖（选择此次付款覆盖哪些阶段）
        </div>
        <div className="flex flex-wrap gap-2">
          {milestones.map((m, mi) => {
            const checked = checkpoint.coversMilestoneIndices.includes(mi);
            const allowedByOthers = allowedMilestoneIndices.has(mi);
            const allowedByContiguity = checked || isContiguousIfAdded(mi);
            const disabled = !allowedByOthers || !allowedByContiguity;
            return (
              <label
                key={mi}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs transition-colors ${
                  checked
                    ? 'border-blue-400 bg-blue-100 text-blue-900 font-medium'
                    : disabled
                      ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 cursor-pointer'
                }`}
                title={
                  !allowedByOthers ? '已被其他付款节点覆盖'
                  : !allowedByContiguity ? '需保持连续选中（只能勾选当前选中区间相邻的里程碑）'
                  : undefined
                }
              >
                <input
                  type="checkbox"
                  className="w-3 h-3"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => onToggleMilestone(mi)}
                />
                <span>{m.title || `M${mi + 1}`}</span>
                <span className="text-[10px] text-slate-500">{m.percent || 0}%</span>
              </label>
            );
          })}

          {/* Tail option — at most one checkpoint may include the tail */}
          <label
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs transition-colors ${
              checkpoint.coversTail
                ? 'border-amber-400 bg-amber-100 text-amber-900 font-medium'
                : hasTailCheckpoint
                  ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                  : 'border-amber-200 bg-white text-amber-800 hover:bg-amber-50 cursor-pointer'
            }`}
            title={hasTailCheckpoint && !checkpoint.coversTail ? '尾款已被其他付款节点覆盖' : undefined}
          >
            <input
              type="checkbox"
              className="w-3 h-3"
              checked={checkpoint.coversTail}
              disabled={hasTailCheckpoint && !checkpoint.coversTail}
              onChange={e => onToggleTail(e.target.checked)}
            />
            <span>尾款</span>
          </label>
        </div>
        {computed && computed.milestoneIndices.length === 0 && !computed.coversTail && (
          <div className="text-[10px] text-rose-600 mt-2">该付款节点未覆盖任何阶段，请至少选择一项。</div>
        )}
      </div>
    </div>
  );
};

export default ContractCreatePage;
